import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdSave, MdRefresh } from 'react-icons/md';
import { useUser } from '../context/UserContext';
import { actualizarMetas, listarMetas } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';

const MESES = [
  { n: 1, corto: 'Ene' },
  { n: 2, corto: 'Feb' },
  { n: 3, corto: 'Mar' },
  { n: 4, corto: 'Abr' },
  { n: 5, corto: 'May' },
  { n: 6, corto: 'Jun' },
  { n: 7, corto: 'Jul' },
  { n: 8, corto: 'Ago' },
  { n: 9, corto: 'Sep' },
  { n: 10, corto: 'Oct' },
  { n: 11, corto: 'Nov' },
  { n: 12, corto: 'Dic' },
];

const PRODUCTOS = [
  { id: 'factoring', label: 'Factoring' },
  { id: 'credito', label: 'Crédito' },
  { id: 'leasing', label: 'Leasing' },
];

const METRICAS_FACTORING = [
  { id: 'META_MET_VOL', label: 'Volumen', hint: 'Suma al KPI Meta Factoring del inicio y al cumplimiento de volumen' },
  { id: 'META_MET_MAR', label: 'Margen', hint: 'Suma al seguimiento comercial (Meta Margen)' },
  { id: 'META_MET_CLI_OPE', label: 'Clientes op.', hint: 'Suma al seguimiento comercial (Meta Clientes)' },
];

const CAMPOS_PRODUCTO = {
  factoring: ['META_MET_VOL', 'META_MET_MAR', 'META_MET_CLI_OPE'],
  credito: ['METAS_CREDITO'],
  leasing: ['METAS_LEASING'],
};

const rowKey = (row) => `${row.EJECUTIVO}||${row.ANIO_MET}||${row.MES_MET}`;
const dirtyKey = (ejecutivo, anio, mes, campo) => `${ejecutivo}||${anio}||${mes}||${campo}`;

const unwrapPayload = (response) => {
  let data = response;
  if (response?.statusCode && response.body != null) {
    data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
  }
  return data && typeof data === 'object' ? data : {};
};

const unwrapFilas = (response) => {
  const data = unwrapPayload(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.filas)) return data.filas;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const filaMetaVacia = (ejecutivo, equipo, plataforma, anio, mes) => ({
  EJECUTIVO: ejecutivo,
  EQUIPO_VENTAS: equipo || '',
  PLATAFORMA: plataforma || '',
  META_MET_MAR: 0,
  META_MET_VOL: 0,
  META_MET_CLI_OPE: 0,
  BASE_CLI_OPE: 0,
  BASE_N_OPE: 0,
  METAS_CREDITO: 0,
  METAS_LEASING: 0,
  ANIO_MET: anio,
  MES_MET: mes,
});

const toNum = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const formatCell = (value, campo) => {
  const n = toNum(value);
  if (campo === 'META_MET_CLI_OPE') {
    return formatNumberEsCL(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return formatNumberEsCL(n, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const parseInput = (raw, campo) => {
  const trimmed = String(raw ?? '').trim().replace(/\s/g, '').replace(',', '.');
  if (trimmed === '') return 0;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (campo === 'META_MET_CLI_OPE') return Math.round(n);
  return n;
};

const currentYear = () => new Date().getFullYear();

const AdministracionMetas = () => {
  const { user } = useUser();
  const [producto, setProducto] = useState('factoring');
  const [anio, setAnio] = useState(currentYear());
  const [mesFiltro, setMesFiltro] = useState(new Date().getMonth() + 1);
  const [metricaFactoring, setMetricaFactoring] = useState('META_MET_VOL');
  const [equipo, setEquipo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [editing, setEditing] = useState(null);
  const [disponibles, setDisponibles] = useState([]);
  const [catalogoListo, setCatalogoListo] = useState(false);
  const [sinFactoring, setSinFactoring] = useState(0);
  const [nuevoEjecutivo, setNuevoEjecutivo] = useState('');
  const [mesNuevo, setMesNuevo] = useState(new Date().getMonth() + 1);
  const [valorNuevo, setValorNuevo] = useState('');
  const [recienAgregados, setRecienAgregados] = useState(() => new Set());

  const campoActivo = producto === 'factoring' ? metricaFactoring : CAMPOS_PRODUCTO[producto][0];

  const loadMetas = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setMessage('');
    try {
      const resp = await listarMetas(user, { anio: Number(anio) });
      const payload = unwrapPayload(resp);
      setRows(unwrapFilas(resp));
      setDisponibles(Array.isArray(payload?.disponibles) ? payload.disponibles : []);
      setCatalogoListo(Array.isArray(payload?.disponibles));
      setSinFactoring(Number(payload?.sin_factoring) || 0);
      setDrafts({});
      setRecienAgregados(new Set());
      setNuevoEjecutivo('');
    } catch (err) {
      setMessage(err?.message || 'No se pudieron cargar las metas');
      setMessageType('error');
      setRows([]);
      setDisponibles([]);
      setCatalogoListo(false);
    } finally {
      setLoading(false);
    }
  }, [user, anio]);

  useEffect(() => {
    loadMetas();
  }, [loadMetas]);

  const equipos = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.EQUIPO_VENTAS) set.add(r.EQUIPO_VENTAS);
    });
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const aniosDisponibles = useMemo(() => {
    const set = new Set(rows.map((r) => Number(r.ANIO_MET)).filter(Boolean));
    set.add(Number(anio));
    set.add(currentYear());
    return [...set].sort((a, b) => b - a);
  }, [rows, anio]);

  const ejecutivos = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.EJECUTIVO)) {
        map.set(r.EJECUTIVO, {
          EJECUTIVO: r.EJECUTIVO,
          EQUIPO_VENTAS: r.EQUIPO_VENTAS || '',
          PLATAFORMA: r.PLATAFORMA || '',
        });
      }
    });
    const q = busqueda.trim().toLowerCase();
    return [...map.values()]
      .filter((e) => !equipo || e.EQUIPO_VENTAS === equipo)
      .filter((e) => {
        if (!q) return true;
        return (
          e.EJECUTIVO.toLowerCase().includes(q) ||
          e.EQUIPO_VENTAS.toLowerCase().includes(q) ||
          e.PLATAFORMA.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.EJECUTIVO.localeCompare(b.EJECUTIVO, 'es'));
  }, [rows, equipo, busqueda]);

  const indexByKey = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => map.set(rowKey(r), r));
    return map;
  }, [rows]);

  const getValue = (ejecutivo, mes, campo) => {
    const dk = dirtyKey(ejecutivo, anio, mes, campo);
    if (Object.prototype.hasOwnProperty.call(drafts, dk)) return drafts[dk];
    const row = indexByKey.get(`${ejecutivo}||${anio}||${mes}`);
    return row ? toNum(row[campo]) : 0;
  };

  const setValue = (ejecutivo, mes, campo, value) => {
    const dk = dirtyKey(ejecutivo, anio, mes, campo);
    setDrafts((prev) => ({ ...prev, [dk]: value }));
  };

  const dirtyCount = Object.keys(drafts).length;

  const mesesVisibles = mesFiltro === 'todos' ? MESES.map((m) => m.n) : [Number(mesFiltro)];

  const totalesMes = useMemo(() => {
    const tot = {};
    mesesVisibles.forEach((mes) => {
      tot[mes] = ejecutivos.reduce((acc, e) => acc + toNum(getValue(e.EJECUTIVO, mes, campoActivo)), 0);
    });
    return tot;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejecutivos, mesesVisibles, campoActivo, drafts, indexByKey, anio]);

  const handleSave = async () => {
    if (!dirtyCount) return;
    const grouped = new Map();
    Object.entries(drafts).forEach(([key, value]) => {
      const [ejecutivo, anioMet, mes, campo] = key.split('||');
      const pk = `${ejecutivo}||${anioMet}||${mes}`;
      if (!grouped.has(pk)) {
        const existing = indexByKey.get(pk) || {};
        grouped.set(pk, {
          EJECUTIVO: ejecutivo,
          EQUIPO_VENTAS: existing.EQUIPO_VENTAS || '',
          PLATAFORMA: existing.PLATAFORMA || '',
          ANIO_MET: Number(anioMet),
          MES_MET: Number(mes),
        });
      }
      grouped.get(pk)[campo] = value;
    });

    setSaving(true);
    setMessage('');
    try {
      await actualizarMetas(user, {
        producto,
        filas: [...grouped.values()],
      });
      setMessage(`Se guardaron ${grouped.size} registro(s) de ${PRODUCTOS.find((p) => p.id === producto)?.label}.`);
      setMessageType('success');
      await loadMetas();
    } catch (err) {
      setMessage(err?.message || 'No se pudieron guardar las metas');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const aplicarValorVisible = () => {
    const parsed = parseInput(bulkValue, campoActivo);
    if (parsed === null) {
      setMessage('Ingresa un número válido para aplicar');
      setMessageType('error');
      return;
    }
    const next = { ...drafts };
    ejecutivos.forEach((e) => {
      mesesVisibles.forEach((mes) => {
        next[dirtyKey(e.EJECUTIVO, anio, mes, campoActivo)] = parsed;
      });
    });
    setDrafts(next);
    setMessage(`Valor aplicado a ${ejecutivos.length} ejecutivo(s) en ${mesesVisibles.length} mes(es). Revisa y guarda.`);
    setMessageType('success');
  };

  const nombresEnGrilla = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const nombre = String(r.EJECUTIVO || '').trim().toUpperCase();
      if (nombre) set.add(nombre);
    });
    return set;
  }, [rows]);

  const opcionesNuevas = useMemo(
    () => disponibles.filter((d) => !nombresEnGrilla.has(String(d.EJECUTIVO || '').trim().toUpperCase())),
    [disponibles, nombresEnGrilla],
  );

  const handleAgregarEjecutivo = () => {
    const elegido = opcionesNuevas.find((d) => d.EJECUTIVO === nuevoEjecutivo);
    if (!elegido) {
      setMessage('Selecciona un ejecutivo que aún no tenga meta en este año');
      setMessageType('error');
      return;
    }
    const mes = Number(mesNuevo);
    if (mes < 1 || mes > 12) {
      setMessage('Selecciona el mes de la meta');
      setMessageType('error');
      return;
    }
    const parsed = valorNuevo.trim() === '' ? 0 : parseInput(valorNuevo, campoActivo);
    if (parsed === null) {
      setMessage('Ingresa un número válido para la meta');
      setMessageType('error');
      return;
    }

    const ejecutivo = String(elegido.EJECUTIVO).trim();
    const equipoVentas = String(elegido.EQUIPO_VENTAS || '').trim();
    const plataforma = String(elegido.PLATAFORMA || '').trim();
    setRows((prev) => {
      const exists = prev.some(
        (r) => String(r.EJECUTIVO).trim().toUpperCase() === ejecutivo.toUpperCase()
          && Number(r.ANIO_MET) === Number(anio)
          && Number(r.MES_MET) === mes,
      );
      if (exists) return prev;
      return [...prev, filaMetaVacia(ejecutivo, equipoVentas, plataforma, Number(anio), mes)];
    });
    setDrafts((prev) => ({
      ...prev,
      [dirtyKey(ejecutivo, anio, mes, campoActivo)]: parsed,
    }));
    setRecienAgregados((prev) => new Set(prev).add(ejecutivo.toUpperCase()));
    setNuevoEjecutivo('');
    setValorNuevo('');
    setMesFiltro(mes);
    if (equipo && equipo !== equipoVentas) setEquipo('');
    if (busqueda) setBusqueda('');
    const nombre = String(elegido.NOMBRE || '').trim();
    const etiqueta = nombre && nombre.toUpperCase() !== ejecutivo.toUpperCase()
      ? `${ejecutivo} (${nombre})`
      : ejecutivo;
    setMessage(`${etiqueta} quedó en ${MESES[mes - 1].corto} ${anio}. Revisa el valor y guarda.`);
    setMessageType('success');
  };

  const hintProducto = {
    factoring: 'Estas metas alimentan Factoring, el detalle por ejecutivo y la Meta Factoring del inicio (volumen).',
    credito: 'METAS_CREDITO suma a la Meta Crédito del inicio y al seguimiento de crédito.',
    leasing: 'METAS_LEASING suma a la Meta Leasing del inicio y al seguimiento de leasing.',
  }[producto];

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Administración de metas</h1>
        <p className="text-gray-600 text-sm">Edición de la tabla metas por producto y mes</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {PRODUCTOS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setProducto(p.id)}
            className="px-4 py-2 rounded-lg text-sm font-semibold border transition-colors"
            style={
              producto === p.id
                ? { background: 'var(--if-primary)', color: 'white', borderColor: 'var(--if-primary)' }
                : { background: 'white', color: '#374151', borderColor: '#d1d5db' }
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="seccion-datos">
        <p className="text-sm text-gray-600 mb-4">{hintProducto}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <label className="text-sm text-gray-700">
            Año
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            >
              {aniosDisponibles.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Mes
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={mesFiltro}
              onChange={(e) => setMesFiltro(e.target.value === 'todos' ? 'todos' : Number(e.target.value))}
            >
              <option value="todos">Todos los meses</option>
              {MESES.map((m) => (
                <option key={m.n} value={m.n}>{m.corto}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Equipo
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={equipo}
              onChange={(e) => setEquipo(e.target.value)}
            >
              <option value="">Todos</option>
              {equipos.map((eq) => (
                <option key={eq} value={eq}>{eq}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Buscar ejecutivo
            <input
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Nombre, equipo o plataforma"
            />
          </label>
          {producto === 'factoring' && (
            <label className="text-sm text-gray-700">
              Meta Factoring
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={metricaFactoring}
                onChange={(e) => setMetricaFactoring(e.target.value)}
              >
                {METRICAS_FACTORING.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {producto === 'factoring' && (
          <p className="text-xs text-gray-500 mb-4">
            {METRICAS_FACTORING.find((m) => m.id === metricaFactoring)?.hint}
          </p>
        )}

        <div className="mb-4 p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50">
          <p className="text-sm font-semibold text-gray-800 mb-1">Crear meta de un ejecutivo nuevo</p>
          <p className="text-xs text-gray-500 mb-3">
            Lista de activos en ejecutivos_estado que no tienen meta en {anio}. El nombre que se guarda es el de Sistema Factoring, el mismo que usa el seguimiento.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm text-gray-700">
              Ejecutivo
              <select
                className="mt-1 block w-80 max-w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                value={nuevoEjecutivo}
                onChange={(e) => setNuevoEjecutivo(e.target.value)}
              >
                <option value="">
                  {opcionesNuevas.length ? 'Seleccionar' : 'No hay ejecutivos sin meta en este año'}
                </option>
                {opcionesNuevas.map((d) => {
                  const nombre = String(d.NOMBRE || '').trim();
                  const distinto = nombre && nombre.toUpperCase() !== String(d.EJECUTIVO).toUpperCase();
                  const detalle = [d.EQUIPO_VENTAS, d.PLATAFORMA].filter(Boolean).join(' · ');
                  return (
                    <option key={d.EJECUTIVO} value={d.EJECUTIVO}>
                      {d.EJECUTIVO}{distinto ? ` — ${nombre}` : ''}{detalle ? ` (${detalle})` : ''}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Mes
              <select
                className="mt-1 block w-28 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                value={mesNuevo}
                onChange={(e) => setMesNuevo(Number(e.target.value))}
              >
                {MESES.map((m) => (
                  <option key={m.n} value={m.n}>{m.corto}</option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Meta del mes
              <input
                className="mt-1 block w-32 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                value={valorNuevo}
                onChange={(e) => setValorNuevo(e.target.value)}
                placeholder="Ej: 12,5"
              />
            </label>
            <button
              type="button"
              className="btn-primary py-2 px-4 text-sm disabled:opacity-50"
              onClick={handleAgregarEjecutivo}
              disabled={!nuevoEjecutivo}
            >
              Agregar a la grilla
            </button>
          </div>
          {sinFactoring > 0 && (
            <p className="text-xs text-amber-700 mt-3">
              {sinFactoring} ejecutivo(s) activo(s) no se pueden agregar porque no tienen nombre de Sistema Factoring.
            </p>
          )}
          {!loading && !catalogoListo && (
            <p className="text-xs text-amber-700 mt-3">
              El listado de ejecutivos nuevos llega al volver a desplegar la lambda de admin-metas.
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-3 mb-4">
          <label className="text-sm text-gray-700">
            Valor para ejecutivos visibles
            <input
              className="mt-1 block w-48 border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={bulkValue}
              onChange={(e) => setBulkValue(e.target.value)}
              placeholder="Ej: 12,5"
            />
          </label>
          <button type="button" className="btn-primary py-2 px-4 text-sm" onClick={aplicarValorVisible}>
            Aplicar a visibles
          </button>
          <button type="button" className="btn-primary py-2 px-4 text-sm" onClick={loadMetas} disabled={loading}>
            <span className="inline-flex items-center gap-1">
              <MdRefresh size={16} /> Recargar
            </span>
          </button>
          <button
            type="button"
            className="btn-success py-2 px-4 text-sm disabled:opacity-50"
            onClick={handleSave}
            disabled={!dirtyCount || saving}
          >
            <span className="inline-flex items-center gap-1">
              <MdSave size={16} />
              {saving ? 'Guardando...' : `Guardar cambios (${dirtyCount})`}
            </span>
          </button>
        </div>

        {message && (
          <div className={`mb-4 p-3 rounded text-sm ${messageType === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
            {message}
          </div>
        )}

        {loading ? (
          <p className="text-gray-500">Cargando metas...</p>
        ) : (
          <div className="tabla-container overflow-x-auto" style={{ maxHeight: '70vh' }}>
            <table className="w-full text-sm" style={{ minWidth: mesFiltro === 'todos' ? '1100px' : '720px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left', position: 'sticky', left: 0, zIndex: 3 }}>Ejecutivo</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Equipo</th>
                  <th style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'left' }}>Plataforma</th>
                  {mesesVisibles.map((mes) => (
                    <th key={mes} style={{ backgroundColor: 'var(--if-primary)', color: 'white', textAlign: 'right' }}>
                      {MESES[mes - 1].corto}
                    </th>
                  ))}
                  {mesFiltro !== 'todos' && (
                    <th style={{ backgroundColor: 'var(--if-primary)', color: 'white' }}>Año</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ejecutivos.map((e) => (
                  <tr key={e.EJECUTIVO}>
                    <td className="font-medium whitespace-nowrap" style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}>
                      {e.EJECUTIVO}
                      {recienAgregados.has(String(e.EJECUTIVO).trim().toUpperCase()) && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                          Nuevo
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap text-gray-600">{e.EQUIPO_VENTAS}</td>
                    <td className="whitespace-nowrap text-gray-600">{e.PLATAFORMA}</td>
                    {mesesVisibles.map((mes) => {
                      const dk = dirtyKey(e.EJECUTIVO, anio, mes, campoActivo);
                      const dirty = Object.prototype.hasOwnProperty.call(drafts, dk);
                      const isEditing = editing?.key === dk;
                      return (
                        <td key={mes} style={{ backgroundColor: dirty ? '#fef3c7' : undefined }}>
                          <input
                            className="w-full text-right border border-transparent hover:border-gray-300 focus:border-blue-400 rounded px-1 py-1 bg-transparent"
                            value={
                              isEditing
                                ? editing.text
                                : formatCell(getValue(e.EJECUTIVO, mes, campoActivo), campoActivo)
                            }
                            onFocus={() => {
                              setEditing({
                                key: dk,
                                text: String(getValue(e.EJECUTIVO, mes, campoActivo)).replace('.', ','),
                              });
                            }}
                            onChange={(ev) => setEditing({ key: dk, text: ev.target.value })}
                            onBlur={() => {
                              const parsed = parseInput(editing?.text, campoActivo);
                              setEditing(null);
                              if (parsed === null) return;
                              setValue(e.EJECUTIVO, mes, campoActivo, parsed);
                            }}
                          />
                        </td>
                      );
                    })}
                    {mesFiltro !== 'todos' && (
                      <td className="text-center text-gray-500">{anio}</td>
                    )}
                  </tr>
                ))}
                {ejecutivos.length === 0 && (
                  <tr>
                    <td colSpan={3 + mesesVisibles.length + (mesFiltro !== 'todos' ? 1 : 0)} className="text-center text-gray-500 py-6">
                      No hay ejecutivos para los filtros seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
              {ejecutivos.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={3} className="font-semibold" style={{ position: 'sticky', left: 0, background: '#f8fafc' }}>
                      Total visible (alimenta seguimiento)
                    </td>
                    {mesesVisibles.map((mes) => (
                      <td key={mes} className="text-right font-semibold" style={{ background: '#f8fafc' }}>
                        {formatCell(totalesMes[mes], campoActivo)}
                      </td>
                    ))}
                    {mesFiltro !== 'todos' && <td style={{ background: '#f8fafc' }} />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdministracionMetas;
